import {
  ActionIcon,
  Badge,
  Button,
  Fieldset,
  Radio,
  Select,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
  emptyInterlocutor,
  InterlocutorFormValues,
} from 'src/Hooks/Forms/useCreatePhysicalContactForm';
import { CompanyContactRole, UpsertInterlocutorDtoTitleEnum } from 'src/Services/API/generated';
import { COMPANY_CONTACT_ROLES, getCompanyRoleLabel } from 'src/Utils/companyContactRoles';

import styles from './InterlocutorsSectionForm.module.scss';
import { Props } from './InterlocutorsSectionForm.types';

const InterlocutorsSectionForm = ({ form }: Props) => {
  const { t } = useTranslation();
  const interlocutors = form.values.interlocutors;

  const roleOptions = COMPANY_CONTACT_ROLES.map(role => ({
    value: role,
    label: getCompanyRoleLabel(t, role),
  }));

  const add = () =>
    form.insertListItem(
      'interlocutors',
      // The first interlocutor is the main contact.
      emptyInterlocutor(
        interlocutors.length === 0 ? CompanyContactRole.Main : CompanyContactRole.Accounting
      )
    );

  // Only one main contact: the previous one takes the role of the new main contact.
  const changeRole = (index: number, role: CompanyContactRole) => {
    const current = interlocutors[index];
    if (role === CompanyContactRole.Main) {
      interlocutors.forEach((other, otherIndex) => {
        if (otherIndex !== index && other.companyRole === CompanyContactRole.Main) {
          form.setFieldValue(`interlocutors.${otherIndex}.companyRole`, current.companyRole);
          form.setFieldValue(
            `interlocutors.${otherIndex}.companyRoleLabel`,
            current.companyRoleLabel
          );
        }
      });
    }
    form.setFieldValue(`interlocutors.${index}.companyRole`, role);
  };

  // The main contact leaves: the oldest remaining one takes over.
  const remove = (index: number) => {
    const removedMain = interlocutors[index].companyRole === CompanyContactRole.Main;
    form.removeListItem('interlocutors', index);
    if (removedMain && interlocutors.length > 1) {
      form.setFieldValue('interlocutors.0.companyRole', CompanyContactRole.Main);
    }
  };

  const displayName = (interlocutor: InterlocutorFormValues) =>
    [interlocutor.firstName, interlocutor.lastName].filter(Boolean).join(' ') ||
    t('contacts.company.newInterlocutor', 'Nouvel interlocuteur');

  return (
    <Fieldset legend={t('contacts.company.interlocutors', 'Interlocuteurs')}>
      <div className={styles.content}>
        {interlocutors.map((interlocutor, index) => (
          <div className={styles.card} key={interlocutor.key}>
            <div className={styles.cardHeader}>
              <span className={styles.cardTitle}>{displayName(interlocutor)}</span>
              {interlocutor.companyRole === CompanyContactRole.Main && (
                <Badge variant="light">{getCompanyRoleLabel(t, CompanyContactRole.Main)}</Badge>
              )}
              <Tooltip
                label={t('contacts.company.removeInterlocutor', 'Retirer cet interlocuteur')}
              >
                <ActionIcon
                  variant="transparent"
                  size={40}
                  className={styles.remove}
                  aria-label={t('contacts.company.removeInterlocutor', 'Retirer cet interlocuteur')}
                  onClick={() => remove(index)}
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Tooltip>
            </div>

            <Radio.Group {...form.getInputProps(`interlocutors.${index}.title`)}>
              <div className={styles.row}>
                <Radio value={UpsertInterlocutorDtoTitleEnum.Mr} label={t('contacts.mr', 'M')} />
                <Radio value={UpsertInterlocutorDtoTitleEnum.Ms} label={t('contacts.ms', 'Mme')} />
              </div>
            </Radio.Group>

            <div className={styles.row}>
              <TextInput
                label={t('contacts.firstName', 'Prénom')}
                {...form.getInputProps(`interlocutors.${index}.firstName`)}
              />
              <TextInput
                label={t('contacts.company.nameOrService', 'Nom ou service')}
                placeholder={t(
                  'contacts.company.nameOrServicePlaceholder',
                  'Ex. Thevignot, Service comptabilité'
                )}
                required
                {...form.getInputProps(`interlocutors.${index}.lastName`)}
              />
            </div>

            <div className={styles.row}>
              <TextInput
                label={t('contacts.phone', 'Téléphone')}
                inputMode="tel"
                {...form.getInputProps(`interlocutors.${index}.phoneNumber`)}
              />
              <TextInput
                label={t('contacts.email', 'Email')}
                inputMode="email"
                {...form.getInputProps(`interlocutors.${index}.email`)}
              />
            </div>

            <div className={styles.row}>
              <Select
                label={t('contacts.company.role', 'Rôle')}
                data={roleOptions}
                allowDeselect={false}
                value={interlocutor.companyRole}
                onChange={value => value && changeRole(index, value as CompanyContactRole)}
              />
              {interlocutor.companyRole === CompanyContactRole.Other ? (
                <TextInput
                  label={t('contacts.company.roleLabel', 'Quel rôle ?')}
                  placeholder={t('contacts.company.roleLabelPlaceholder', 'Ex. Commercial')}
                  required
                  {...form.getInputProps(`interlocutors.${index}.companyRoleLabel`)}
                />
              ) : (
                <div />
              )}
            </div>
          </div>
        ))}

        <Button
          variant="outline"
          leftSection={<IconPlus size={18} />}
          className={styles.addButton}
          onClick={add}
        >
          {t('contacts.company.addInterlocutor', 'Ajouter un interlocuteur')}
        </Button>
      </div>
    </Fieldset>
  );
};

export default InterlocutorsSectionForm;
